import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  getDocs,
  Timestamp,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Conversation, ConversationMember, ChatMessage, COLLECTION_NAMES, User } from '../types';

export interface ConversationWithDetails extends Conversation {
  otherUser?: {
    id: string;
    name: string;
    email: string;
    photoURL?: string;
  };
  membersDetails?: Array<{
    id: string;
    name: string;
    email: string;
    photoURL?: string;
  }>;
  lastMessage?: {
    content: string;
    createdAt: Timestamp;
    userName: string;
  };
  unreadCount: number;
  lastReadAt?: Timestamp;
}

/**
 * Hook for managing conversations (direct messages and group chats)
 */
export function useConversations(userId: string | undefined) {
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load conversations with real-time listener
  useEffect(() => {
    if (!userId) {
      setConversations([]);
      setLoading(false);
      return;
    }

    // Query all conversations - we'll filter by membership client-side
    // since Firestore doesn't support array-contains queries efficiently for this
    const conversationsQuery = query(
      collection(db, COLLECTION_NAMES.CONVERSATIONS),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      conversationsQuery,
      async (snapshot) => {
        try {
          const conversationsData: ConversationWithDetails[] = [];

          // Process each conversation
          for (const conversationDoc of snapshot.docs) {
            const conversationData = conversationDoc.data();
            const conversationId = conversationDoc.id;

            // Check if user is a member
            if (!conversationData.members?.includes(userId)) {
              continue;
            }

            // Get member info to find lastReadAt
            const memberRef = doc(
              db,
              COLLECTION_NAMES.CONVERSATIONS,
              conversationId,
              COLLECTION_NAMES.CONVERSATION_MEMBERS,
              userId
            );
            const memberDoc = await getDoc(memberRef);
            const memberData = memberDoc.data() as ConversationMember | undefined;
            const lastReadAt = memberData?.lastReadAt;

            // Get other members (for direct messages)
            const otherMemberIds = conversationData.members.filter(
              (id: string) => id !== userId
            );

            // Get other user info for direct messages
            let otherUser: ConversationWithDetails['otherUser'] | undefined;
            if (conversationData.type === 'direct' && otherMemberIds.length > 0) {
              const otherUserId = otherMemberIds[0];
              const otherUserRef = doc(db, COLLECTION_NAMES.USERS, otherUserId);
              const otherUserDoc = await getDoc(otherUserRef);
              if (otherUserDoc.exists()) {
                const otherUserData = otherUserDoc.data() as User;
                otherUser = {
                  id: otherUserId,
                  name: otherUserData.name || otherUserData.Name || '',
                  email: otherUserData.email || otherUserData.Email || '',
                  photoURL: otherUserData.photo || otherUserData.photoURL,
                };
              }
            }

            // Get last message (GroupChat uses 'timestamp' field)
            const messagesQuery = query(
              collection(
                db,
                COLLECTION_NAMES.CONVERSATIONS,
                conversationId,
                COLLECTION_NAMES.CHAT_MESSAGES
              ),
              orderBy('timestamp', 'desc'),
              limit(1)
            );

            let lastMessage: ConversationWithDetails['lastMessage'] | undefined;
            let unreadCount = 0;

            try {
              const messagesSnapshot = await getDocs(messagesQuery);
              if (!messagesSnapshot.empty) {
                const lastMsgDoc = messagesSnapshot.docs[0];
                const lastMsgData = lastMsgDoc.data() as ChatMessage;

                // Get user info for last message
                // GroupChat stores user info in message.user, but we can also fetch from users collection
                const userIdFromMessage = lastMsgData.user?.uid || lastMsgData.userId;
                let lastMsgUserData: User | undefined;
                if (userIdFromMessage) {
                  const lastMsgUserRef = doc(db, COLLECTION_NAMES.USERS, userIdFromMessage);
                  const lastMsgUserDoc = await getDoc(lastMsgUserRef);
                  lastMsgUserData = lastMsgUserDoc.data() as User;
                }

                lastMessage = {
                  content: lastMsgData.message || lastMsgData.content || '',
                  createdAt: lastMsgData.timestamp || lastMsgData.createdAt || Timestamp.now(),
                  userName: lastMsgUserData?.name || lastMsgUserData?.Name || 'Unknown',
                };

                // Calculate unread count (messages after lastReadAt)
                // Note: Firestore doesn't support != queries efficiently, so we fetch all and filter client-side
                try {
                  const allMessagesQuery = query(
                    collection(
                      db,
                      COLLECTION_NAMES.CONVERSATIONS,
                      conversationId,
                      COLLECTION_NAMES.CHAT_MESSAGES
                    ),
                    orderBy('timestamp', 'desc')
                  );
                  const allMessagesSnapshot = await getDocs(allMessagesQuery);
                  
                  // Filter client-side: messages not from current user
                  const unreadMessages = allMessagesSnapshot.docs.filter((msgDoc) => {
                    const msgData = msgDoc.data();
                    const msgUserId = msgData.user?.uid || msgData.userId;
                    const msgTimestamp = msgData.timestamp;
                    
                    // Must be from another user
                    if (msgUserId === userId) return false;
                    
                    // If we have lastReadAt, only count messages after that
                    if (lastReadAt && msgTimestamp) {
                      return msgTimestamp.toMillis() > lastReadAt.toMillis();
                    }
                    
                    // If no lastReadAt, count all messages from other users
                    return true;
                  });
                  
                  unreadCount = unreadMessages.length;
                } catch (unreadError) {
                  // If query fails (e.g., no index), set unread count to 0
                  console.warn('Error calculating unread count:', unreadError);
                  unreadCount = 0;
                }
              }
            } catch (msgError) {
              // If messages query fails (e.g., no index), just continue without last message
              console.warn('Error fetching last message:', msgError);
            }

            // Get member details for group chats
            let membersDetails: ConversationWithDetails['membersDetails'] | undefined;
            if (conversationData.type === 'group') {
              const memberDetailsPromises = conversationData.members.map(async (memberId: string) => {
                const memberUserRef = doc(db, COLLECTION_NAMES.USERS, memberId);
                const memberUserDoc = await getDoc(memberUserRef);
                if (memberUserDoc.exists()) {
                  const memberUserData = memberUserDoc.data() as User;
                  return {
                    id: memberId,
                    name: memberUserData.name || memberUserData.Name || 'Unknown',
                    email: memberUserData.email || memberUserData.Email || '',
                    photoURL: memberUserData.photo || memberUserData.photoURL,
                  };
                }
                return null;
              });
              
              const memberDetailsResults = await Promise.all(memberDetailsPromises);
              membersDetails = memberDetailsResults.filter((detail) => detail !== null) as Array<{
                id: string;
                name: string;
                email: string;
                photoURL?: string;
              }>;
            }

            conversationsData.push({
              id: conversationId,
              type: conversationData.type || 'direct',
              isPrivate: conversationData.isPrivate ?? true,
              createdAt: conversationData.createdAt || Timestamp.now(),
              updatedAt: conversationData.updatedAt || Timestamp.now(),
              lastMessageAt: conversationData.lastMessageAt,
              members: conversationData.members || [],
              otherUser,
              membersDetails,
              lastMessage,
              unreadCount,
              lastReadAt: lastReadAt || undefined,
            });
          }

          // Sort by updatedAt (most recent first)
          conversationsData.sort((a, b) => {
            const aTime = a.lastMessageAt?.toMillis() || a.updatedAt.toMillis();
            const bTime = b.lastMessageAt?.toMillis() || b.updatedAt.toMillis();
            return bTime - aTime;
          });

          setConversations(conversationsData);
          setLoading(false);
        } catch (err: any) {
          console.error('Error processing conversations:', err);
          setError(err.message);
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error loading conversations:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  /**
   * Check if a conversation with the exact same members already exists
   */
  const findExistingConversation = async (
    memberIds: string[],
    type: 'direct' | 'group' = 'group'
  ): Promise<string | null> => {
    if (!userId) return null;

    try {
      // Sort member IDs for consistent comparison
      const sortedMembers = [...memberIds].sort();
      
      // Query conversations where current user is a member
      const existingConversationsQuery = query(
        collection(db, COLLECTION_NAMES.CONVERSATIONS),
        where('type', '==', type),
        where('members', 'array-contains', userId)
      );

      const existingSnapshot = await getDocs(existingConversationsQuery);
      
      for (const existingDoc of existingSnapshot.docs) {
        const data = existingDoc.data();
        const existingMembers = data.members || [];
        
        // Sort existing members for comparison
        const sortedExisting = [...existingMembers].sort();
        
        // Check if members match exactly (same length and same IDs)
        if (
          sortedMembers.length === sortedExisting.length &&
          sortedMembers.every((id, index) => id === sortedExisting[index])
        ) {
          // Exact match found, return existing conversation ID
          return existingDoc.id;
        }
      }

      return null;
    } catch (err: any) {
      console.error('Error finding existing conversation:', err);
      return null;
    }
  };

  /**
   * Create a new direct message conversation
   */
  const createDirectMessage = async (otherUserId: string): Promise<string> => {
    if (!userId) throw new Error('User not authenticated');

    try {
      // Check if conversation already exists
      const existingId = await findExistingConversation([userId, otherUserId], 'direct');
      if (existingId) {
        return existingId;
      }

      // Create new conversation
      const conversationData: Omit<Conversation, 'id'> = {
        type: 'direct',
        isPrivate: true,
        members: [userId, otherUserId],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const conversationRef = doc(collection(db, COLLECTION_NAMES.CONVERSATIONS));
      await setDoc(conversationRef, conversationData);

      // Create member records
      const memberData: Omit<ConversationMember, 'id'> = {
        conversationId: conversationRef.id,
        userId: userId,
        joinedAt: Timestamp.now(),
      };

      const otherMemberData: Omit<ConversationMember, 'id'> = {
        conversationId: conversationRef.id,
        userId: otherUserId,
        joinedAt: Timestamp.now(),
      };

      await setDoc(
        doc(
          db,
          COLLECTION_NAMES.CONVERSATIONS,
          conversationRef.id,
          COLLECTION_NAMES.CONVERSATION_MEMBERS,
          userId
        ),
        memberData
      );

      await setDoc(
        doc(
          db,
          COLLECTION_NAMES.CONVERSATIONS,
          conversationRef.id,
          COLLECTION_NAMES.CONVERSATION_MEMBERS,
          otherUserId
        ),
        otherMemberData
      );

      return conversationRef.id;
    } catch (err: any) {
      console.error('Error creating direct message:', err);
      throw err;
    }
  };

  /**
   * Create a new group chat conversation
   */
  const createGroupChat = async (
    memberIds: string[],
    name?: string
  ): Promise<string> => {
    if (!userId) throw new Error('User not authenticated');
    if (memberIds.length < 2) {
      throw new Error('Group chat must have at least 2 members');
    }

    try {
      // Ensure current user is included
      const allMembers = Array.from(new Set([userId, ...memberIds]));
      
      // Check if conversation with exact same members already exists
      const existingId = await findExistingConversation(allMembers, 'group');
      if (existingId) {
        return existingId;
      }

      // Create new conversation
      const conversationData: any = {
        type: 'group',
        isPrivate: true,
        members: allMembers,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      // Only include name if it's provided (Firestore doesn't allow undefined)
      if (name && name.trim()) {
        conversationData.name = name.trim();
      }

      const conversationRef = doc(collection(db, COLLECTION_NAMES.CONVERSATIONS));
      await setDoc(conversationRef, conversationData);

      // Create member records for all members
      const memberPromises = allMembers.map((memberId) => {
        const memberData: Omit<ConversationMember, 'id'> = {
          conversationId: conversationRef.id,
          userId: memberId,
          joinedAt: Timestamp.now(),
        };

        return setDoc(
          doc(
            db,
            COLLECTION_NAMES.CONVERSATIONS,
            conversationRef.id,
            COLLECTION_NAMES.CONVERSATION_MEMBERS,
            memberId
          ),
          memberData
        );
      });

      await Promise.all(memberPromises);

      return conversationRef.id;
    } catch (err: any) {
      console.error('Error creating group chat:', err);
      throw err;
    }
  };

  /**
   * Add members to an existing conversation
   */
  const addMembersToConversation = async (
    conversationId: string,
    newMemberIds: string[]
  ): Promise<string> => {
    if (!userId) throw new Error('User not authenticated');

    try {
      // Get current conversation
      const conversationRef = doc(db, COLLECTION_NAMES.CONVERSATIONS, conversationId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationDoc.data() as Conversation;
      const currentMembers = conversationData.members || [];
      
      // Add new members (avoid duplicates)
      const updatedMembers = Array.from(new Set([...currentMembers, ...newMemberIds]));
      
      // Check if this would create a duplicate conversation
      const existingId = await findExistingConversation(updatedMembers, conversationData.type);
      if (existingId && existingId !== conversationId) {
        // Duplicate exists, return that conversation ID instead
        return existingId;
      }

      // Update conversation members
      await updateDoc(conversationRef, {
        members: updatedMembers,
        updatedAt: serverTimestamp(),
      });

      // Create member records for new members
      const newMemberPromises = newMemberIds.map((memberId) => {
        // Check if member record already exists
        const memberRef = doc(
          db,
          COLLECTION_NAMES.CONVERSATIONS,
          conversationId,
          COLLECTION_NAMES.CONVERSATION_MEMBERS,
          memberId
        );

        return setDoc(
          memberRef,
          {
            conversationId: conversationId,
            userId: memberId,
            joinedAt: serverTimestamp(),
          },
          { merge: true } // Merge in case it already exists
        );
      });

      await Promise.all(newMemberPromises);

      return conversationId;
    } catch (err: any) {
      console.error('Error adding members to conversation:', err);
      throw err;
    }
  };

  /**
   * Update lastReadAt for a conversation
   */
  const markAsRead = async (conversationId: string) => {
    if (!userId) return;

    try {
      const memberRef = doc(
        db,
        COLLECTION_NAMES.CONVERSATIONS,
        conversationId,
        COLLECTION_NAMES.CONVERSATION_MEMBERS,
        userId
      );

      await updateDoc(memberRef, {
        lastReadAt: serverTimestamp(),
      });
    } catch (err: any) {
      console.error('Error marking conversation as read:', err);
    }
  };

  return {
    conversations,
    loading,
    error,
    createDirectMessage,
    createGroupChat,
    addMembersToConversation,
    markAsRead,
  };
}

